import { Typography, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { ComponentType, ForwardedRef, forwardRef } from 'react';

import { IconProps } from '@/components/icons/ThumbsUpIcon';
import { Loader } from '@/components/progress/Loader/Loader';
import { useViewOnly } from '@/contexts/InteractionParadigm.context';

import { ButtonRoot } from './ButtonRoot';
import { getCSSPropertiesByVariant } from './styles';
import { ButtonSize, ButtonVariant } from './types';

export type IconButtonSize = Exclude<ButtonSize, '_bg'>;

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ComponentType<IconProps>;
  ariaLabel: string;
  /**
   * A visible label below the icon, as in a vertical tool rail. Without a
   * label, the button shows only the icon.
   */
  label?: string;
  iconProps?: Partial<IconProps>;
  variant?: ButtonVariant;
  size: IconButtonSize;
  disabled?: boolean;
  loading?: boolean;
  sx?: SxProps<Theme>;
}

export const iconSizesByButtonSize: Record<IconButtonSize, number> = {
  xs: 16,
  sm: 16,
  md: 16,
};

// This is a generic icon button for use with thumbs up and thumbs down icons or a
// similar pair of icons that represent a positive and negative action.
function IconButtonInner(
  {
    iconProps,
    icon: Icon,
    ariaLabel,
    label,
    loading,
    variant = 'secondary',
    sx,
    ...buttonProps
  }: IconButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const { size } = buttonProps;
  const theme = useTheme();
  const isViewOnly = useViewOnly();
  const iconSize = iconSizesByButtonSize[size];
  const computedDisabled = buttonProps.disabled || !!loading;
  const variantStyle = getCSSPropertiesByVariant(
    variant,
    computedDisabled,
    theme,
    isViewOnly
  );
  return (
    <ButtonRoot
      ref={ref}
      aria-label={ariaLabel}
      square={!label}
      variant={variant}
      isViewOnly={isViewOnly}
      {...buttonProps}
      sx={[
        !!label && { flexDirection: 'column', gap: '4px' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Icon
        size={iconSize}
        {...iconProps}
        sx={{
          ...iconProps?.sx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          visibility: loading ? 'hidden' : 'inherit',
        }}
      />
      {label && (
        <Typography
          variant="body-xs"
          component="span"
          // A label below an icon is smaller than the smallest body text,
          // and uses the text color of the button.
          sx={{
            fontSize: 10,
            lineHeight: '12px',
            color: 'inherit',
            visibility: loading ? 'hidden' : 'inherit',
          }}
        >
          {label}
        </Typography>
      )}
      {loading && (
        <Loader
          boxProps={{
            sx: {
              display: 'flex',
              position: 'absolute',
            },
          }}
          circularProgressProps={{
            size: iconSize,
            sx: {
              color: variantStyle.color,
            },
          }}
        />
      )}
    </ButtonRoot>
  );
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  IconButtonInner
);
